import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Panel, StatusBadge } from "@/components/admin/common";
import { BookingDialog } from "@/components/admin/BookingDialog";
import { LeadActions } from "@/components/admin/leads/LeadActions";
import { FollowBadge } from "@/components/admin/leads/LeadBits";
import { StatusSelect } from "@/components/admin/leads/LeadBits";
import { LeadInfoForm } from "@/components/admin/leads/LeadInfoForm";
import { LeadTimeline } from "@/components/admin/leads/LeadTimeline";
import { LeadHistory } from "@/components/admin/leads/LeadHistory";

const ConvertButton = ({ lead, customerId, onDone }) => {
  if (customerId) {
    return <Button asChild variant="outline" data-testid="lead-customer-link"><Link to="/admin/customers"><UserCheck className="h-4 w-4 mr-2 text-emerald-600" />Customer</Link></Button>;
  }
  const ok = ["booked", "completed"].includes(lead.status);
  const convert = async () => {
    try { await api.post(`/admin/enquiries/${lead.id}/convert`); toast.success("Converted to customer"); onDone(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return <Button onClick={convert} disabled={!ok} title={ok ? "" : "Move to Confirmed/Booked first"} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lead-convert-button"><UserCheck className="h-4 w-4 mr-2" />Convert to Customer</Button>;
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [h, setH] = useState(null);
  const [booking, setBooking] = useState(false);
  const load = useCallback(() => api.get(`/admin/enquiries/${id}/history`).then((r) => setH(r.data)).catch(() => toast.error("Lead not found")), [id]);
  useEffect(() => { load(); }, [load]);
  const setLead = (lead) => setH((x) => ({ ...x, lead }));

  const patch = async (body, msg = "Lead updated") => {
    try { const { data } = await api.patch(`/admin/enquiries/${id}`, body); setLead(data); toast.success(msg); } catch (e) { toast.error(formatApiError(e)); }
  };
  const done = async () => { const { data } = await api.post(`/admin/enquiries/${id}/follow-up/done`); setLead(data); toast.success("Follow-up marked done"); };
  const remove = async () => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    toast.success("Lead deleted");
    navigate("/admin/enquiries");
  };

  if (!h) return <Loader2 className="h-6 w-6 animate-spin text-ocean" />;
  const l = h.lead;
  return (
    <div data-testid="lead-detail-page">
      <Link to="/admin/enquiries" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-ocean mb-3" data-testid="lead-back-link"><ArrowLeft className="h-4 w-4" />All leads</Link>
      <PageHeader title={l.name} subtitle={`${l.phone}${l.email ? ` · ${l.email}` : ""}`}>
        <ConvertButton lead={l} customerId={h.customer_id} onDone={load} />
      </PageHeader>
      <Panel className="p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="lead-detail-toolbar">
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status={l.status} testId="lead-detail-status-badge" />
          <StatusSelect lead={l} onChange={(_, status) => patch({ status }, "Stage updated")} className="h-9 w-[180px]" />
          <FollowBadge lead={l} />
          {l.follow_up_date && <Button size="sm" variant="outline" onClick={done} data-testid="lead-followup-done-button"><CheckCircle2 className="h-4 w-4 mr-1.5" />Follow-up done</Button>}
        </div>
        <LeadActions lead={l} onBook={() => setBooking(true)} onDelete={remove} />
      </Panel>
      <div className="grid gap-6 xl:grid-cols-12">
        <div className="xl:col-span-5 space-y-6"><LeadInfoForm lead={l} onSave={patch} /></div>
        <div className="xl:col-span-7 space-y-6">
          <LeadTimeline lead={l} onAdded={setLead} />
          <LeadHistory h={h} />
        </div>
      </div>
      <BookingDialog open={booking} onOpenChange={setBooking} enquiry={l} onSaved={() => { setBooking(false); load(); }} />
    </div>
  );
}
